import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import api from '../../../api/axios'
import endpoints from '../../../api/endpoints'
import { extractApiErrorMessage } from '../../../utils/apiError'
import { RequestStatusCode } from '../../../constants/RequestStatus'
import { getStatusLabel } from '../../../utils/requestStatus'

// ============================================================================
// Calendar View - POST /tort/time-off-requests/calendar-view
// Returns calendar week data with time off requests.
//
// Required:
//  - x-nuid       (header)  -> added automatically by the axios interceptor
//  - selectedDate (body)    -> YYYY-MM-DD
//
// Optional filters (when provided, dateOption/status/requestType are required):
//  - dateOption   "A" (All Dates) | "R" (Custom Range)
//  - startDate / endDate          -> required when dateOption === "R"
//  - department / union / approvalGroup / jobCode -> optional string filters
//  - status       array of request statuses
//  - requestType  array of request type codes
// ============================================================================

/** dateOption: A = All Dates, R = Custom Range */
export type CalendarDateOption = 'A' | 'R'

// Use RequestStatusCode from constants for calendar status
export type CalendarRequestStatus = RequestStatusCode

/** V=Vacation, F=Float, D=FPD, O=Other, A=Vacation Bid, B=Birthday */
export type CalendarRequestType = 'V' | 'F' | 'D' | 'O' | 'A' | 'B'

export interface CalendarViewFilters {
  /** Required when filters object is provided */
  dateOption: CalendarDateOption
  /** Required when dateOption === 'R' */
  startDate?: string
  /** Required when dateOption === 'R' */
  endDate?: string
  department?: string[]
  union?: string[]
  approvalGroup?: string[]
  jobCode?: string[]
  primaryApprover?: string[]
  /** Required when filters object is provided */
  status: CalendarRequestStatus[]
  /** Required when filters object is provided */
  requestType: CalendarRequestType[]
}

export interface CalendarViewPayload {
  /** YYYY-MM-DD */
  selectedDate: string
  filters?: CalendarViewFilters
}


export interface CalendarViewRequestItem {
  /** Stable row identity for calendar grid grouping */
  rowId?: string
  employeeName?: string
  /** YYYY-MM-DD */
  date?: string
  /** Status code, e.g. "APPROVED", "SUBMITTED_ONHOLD" */
  status?: string
  /** Request type code, e.g. "V", "F" */
  requestType?: string
  /** Optional display text for the calendar cell (e.g. partial-day time range) */
  cellText?: string
}

export interface CalendarViewEmployee {
  employeeName?: string
  requests?: Array<{
    date?: string
    status?: string
    requestType?: string
  }>
}

export interface CalendarViewDayItem {
  /** YYYY-MM-DD */
  date?: string
  hours?: number
  time?: {
    type?: string
    [key: string]: unknown
  }
}

export interface CalendarViewApiItem {
  toffReqstId?: string
  empNUID?: string
  empName?: string
  status?: string
  /** Raw DB status code (e.g. "SUBMITTED", "PAPPROVED"). Preferred over `status` for mapping. */
  statusCode?: string
  days?: CalendarViewDayItem[]
  [key: string]: unknown
}

export interface CalendarViewResponse {
  weekStart?: string
  weekEnd?: string
  weekStartDate?: string
  weekEndDate?: string
  employees?: CalendarViewEmployee[]
  data?: CalendarViewRequestItem[]
  items?: CalendarViewApiItem[]
  [key: string]: unknown
}

export const mapCalendarViewResponse = (
  response: CalendarViewResponse | null | undefined
): Array<{
  rowId: string
  employeeName: string
  date: string
  status: string
  cellText?: string
}> => {
  if (!response) return []

  const CALENDAR_STATUS_ALIAS_TO_CODE: Record<string, string> = {
    // Submitted group
    FORWARDED: 'SUBMITTED',
    SUBMITTED_RELEASED: 'SUBMITTED',

    // Resubmitted group
    RESUBMITTED_FORWARDED: 'RESUBMITTED',
    RESUBMITTED_RELEASED: 'RESUBMITTED',

    // Approved group
    PAPPROVED: 'APPROVED',
    RTDENIED: 'APPROVED',
    PRTDENIED: 'APPROVED',
    APPROVED_PRTSUBMITTED: 'APPROVED',
    OVERRIDDEN: 'APPROVED',
    PAPPROVED_RTDENIED: 'APPROVED',
    OVERRIDDEN_RTDENIED: 'APPROVED',

    // On Hold group
    ON_HOLD: 'SUBMITTED_ONHOLD',
    ONHOLD: 'SUBMITTED_ONHOLD',
    RESUBMITTED_ONHOLD: 'SUBMITTED_ONHOLD',
  }

  const CALENDAR_ALLOWED_STATUS_LABELS = new Set([
    'Submitted',
    'Resubmitted',
    'Approved',
    'On Hold',
    'Returned',
  ])

  const toLabel = (status?: string): string => {
    if (!status) return ''
    const normalized = String(status).trim().toUpperCase().replaceAll(/\s+/g, '_')
    const normalizedStatusCode = CALENDAR_STATUS_ALIAS_TO_CODE[normalized] ?? normalized
    const label = getStatusLabel(normalizedStatusCode)
    return CALENDAR_ALLOWED_STATUS_LABELS.has(label) ? label : ''
  }

  const rows: Array<{
    rowId: string
    employeeName: string
    date: string
    status: string
    cellText?: string
  }> = []

  const toMeridiemText = (timeValue?: string): { time: string; suffix: string } => {
    if (!timeValue) return { time: '', suffix: '' }

    const [hourPart, minutePart = '00'] = String(timeValue).split(':')
    const hour24 = Number.parseInt(hourPart, 10)
    const minute = Number.parseInt(minutePart, 10)

    if (Number.isNaN(hour24) || Number.isNaN(minute)) {
      return { time: String(timeValue), suffix: '' }
    }

    const suffix = hour24 >= 12 ? ' PM' : ' AM'
    const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12
    const minuteText = String(minute).padStart(2, '0')
    return { time: `${hour12}.${minuteText}`, suffix }
  }

  const getPartialCellText = (day: CalendarViewDayItem): string | undefined => {
    const timeType = String(day.time?.type || '').toUpperCase()
    if (timeType !== 'PARTIAL') return undefined

    const startParsed = toMeridiemText(day.time?.start as string | undefined)
    const endParsed = toMeridiemText(day.time?.end as string | undefined)
    if (!startParsed.time || !endParsed.time) return undefined
    // Format: "9.00-8.00am" (only show suffix at end)
    return `${startParsed.time}${startParsed.suffix} - ${endParsed.time}${endParsed.suffix}`
  }

  // Shape 1: employees[] each with nested requests[]
  if (Array.isArray(response.employees)) {
    response.employees.forEach((employee) => {
      const name = employee.employeeName ?? ''
      ;(employee.requests ?? []).forEach((req) => {
        if (!name || !req.date) return
        const mappedStatus = toLabel(req.status)
        if (!mappedStatus) return
        rows.push({
          rowId: `${name}-${req.status ?? ''}`,
          employeeName: name,
          date: req.date,
          status: mappedStatus,
        })
      })
    })
  }

  // Shape 2: flat list under `data` (or the root being an array)
  let flatList: CalendarViewRequestItem[] = []
  if (Array.isArray(response.data)) {
    flatList = response.data
  } else if (Array.isArray(response)) {
    flatList = response as CalendarViewRequestItem[]
  }

  flatList.forEach((item) => {
    if (!item.employeeName || !item.date) return
    const mappedStatus = toLabel(item.status)
    if (!mappedStatus) return
    const rowId = item.rowId || `${item.employeeName}-${item.status ?? ''}`
    rows.push({
      rowId,
      employeeName: item.employeeName,
      date: item.date,
      status: mappedStatus,
      cellText: item.cellText,
    })
  })


  if (Array.isArray(response.items)) {
    response.items.forEach((item, index) => {
      const employeeName = item.empName ?? ''
      // Prefer statusCode (raw DB code e.g. "PAPPROVED") over status (display label e.g. "Approved")
      // so the alias map can correctly normalise all DB variants to their calendar colour.
      const status = toLabel(item.statusCode ?? item.status)
      if (!status) return
      const rowId = item.toffReqstId || `${employeeName}-${status}-${index}`
      ;(item.days ?? []).forEach((day) => {
        if (!employeeName || !day.date) return
        rows.push({
          rowId,
          employeeName,
          date: day.date,
          status,
          cellText: getPartialCellText(day),
        })
      })
    })
  }

  return rows
}


export type CalendarFilterOption =
  | string
  | {
      code?: string
      value?: string
      id?: string
      nuid?: string
      description?: string
      label?: string
      name?: string
      /** Approval group this approver belongs to (Float Employee support: an
       * employee can now have more than one approval group, so each approver
       * row carries its own group id instead of assuming a single shared one). */
      approvalGrpId?: string
    }

export interface CalendarFiltersResponse {
  departments?: CalendarFilterOption[]
  unions?: CalendarFilterOption[]
  approvers?: CalendarFilterOption[]
  approvalGroups?: CalendarFilterOption[]
  jobCodes?: CalendarFilterOption[]
  statuses?: CalendarFilterOption[]
  requestTypes?: CalendarFilterOption[]
  [key: string]: unknown
}

/** Shape consumed by FilterComponent / CheckBoxFilter. */
export interface CheckboxFilterGroup {
  title: string
  labels: string[]
  disabledLabels?: string[]
}

export type CheckboxFiltersConfig = Record<string, CheckboxFilterGroup>

/**
 * Default Status and Request Type options. These are fixed enums (per the
 * calendar-view POST contract), so they are used unless the API overrides them.
 */
const DEFAULT_STATUS_LABELS = [
  'All',
  'Submitted',
  'Approved',
  'Denied',
  'On Hold',
  'Withdrawn',
  'Retraction Submitted',
  'Retracted',
  'Resubmitted',
  'Returned',
]

const DEFAULT_REQUEST_TYPE_LABELS = [
  'All Request Types',
  'Vacation',
  'FLOAT',
  'FPD',
  'OTHER',
  'Vacation Bid',
  'Birthday',
]

/** Normalize a single option (string or object) into a display label. */
const toOptionLabel = (option: CalendarFilterOption): string => {
  if (typeof option === 'string') return option
  if (!option) return ''
  const code = option.code ?? option.value ?? option.id ?? option.nuid
  const description = option.description ?? option.label ?? option.name
  if (code && description) return `${code} - ${description}`
  return String(code ?? description ?? '')
}

/** Normalize a list of options, dropping empty entries. */
const toLabelList = (
  options: CalendarFilterOption[] | undefined,
  { withAll = true }: { withAll?: boolean } = {}
): string[] => {
  const labels = (options ?? []).map(toOptionLabel).filter(Boolean)
  if (withAll && !labels.some((l) => /^all/i.test(l))) {
    return ['All', ...labels]
  }
  return labels
}

/** Merge multiple option lists into one de-duplicated, display-ready label list. */
const toMergedLabelList = (
  optionGroups: Array<CalendarFilterOption[] | undefined>,
  { withAll = true }: { withAll?: boolean } = {}
): string[] => {
  const merged = optionGroups.flatMap((group) => group ?? [])
  const labels = toLabelList(merged, { withAll: false })
  const uniqueLabels = Array.from(new Set(labels))
  if (withAll && !uniqueLabels.some((label) => /^all/i.test(label))) {
    return ['All', ...uniqueLabels]
  }
  return uniqueLabels
}

/**
 * Build approver labels using each approver's own approval group code.
 * Example: "Camille Bauer - 00053997"
 *
 * A Float Employee can have more than one approval group (their own group
 * plus any group they've been float-assigned to), so each approver entry is
 * paired with its own `approvalGrpId` rather than a single group code shared
 * across the whole list — otherwise every approver would be mislabeled with
 * whichever group happened to come first. Falls back to the first known
 * group code when an approver entry has no `approvalGrpId` of its own
 * (legacy single-group payload shape), preserving prior behavior.
 */
const toApprovalGroupApproverLabels = (
  approvers: CalendarFilterOption[] | undefined,
  approvalGroups: CalendarFilterOption[] | undefined
): string[] => {
  const groupCodes = (approvalGroups ?? [])
    .map((group) => toOptionLabel(group).split(' - ')[0]?.trim())
    .filter((code): code is string => Boolean(code))

  if (groupCodes.length === 0) {
    return toMergedLabelList([approvers, approvalGroups], { withAll: true })
  }

  const fallbackGroupCode = groupCodes[0]
  const coveredGroupCodes = new Set<string>()

  const approverLabels = (approvers ?? [])
    .map((approver) => {
      if (typeof approver === 'string') {
        coveredGroupCodes.add(fallbackGroupCode)
        return `${approver} - ${fallbackGroupCode}`
      }
      const approverName = approver.name ?? approver.label ?? approver.description
      if (!approverName) return ''
      const groupCode = approver.approvalGrpId ?? fallbackGroupCode
      coveredGroupCodes.add(groupCode)
      return `${approverName} - ${groupCode}`
    })
    .filter(Boolean)

  // Groups with no matching approver row (e.g. a group with no designated
  // primary approver) still need to appear as a selectable option.
  const uncoveredGroupLabels = groupCodes.filter(
    (code) => !coveredGroupCodes.has(code)
  )

  const allLabels = Array.from(new Set([...approverLabels, ...uncoveredGroupLabels]))

  if (allLabels.length === 0) {
    return toLabelList(approvalGroups, { withAll: true })
  }

  return ['All', ...allLabels]
}

/**
 * Maps the calendar-filters API response into the config shape consumed by
 * `FilterComponent`. Status and Request Type fall back to fixed defaults when
 * the API does not supply them.
 */
export const mapCalendarFiltersResponse = (
  response: CalendarFiltersResponse | null | undefined
): CheckboxFiltersConfig => {
  return {
    departmentFilter: {
      title: 'Department Code-Description',
      labels: toLabelList(response?.departments),
    },
    unionAffiliationFilter: {
      title: 'Union Affiliation',
      labels: toLabelList(response?.unions),
    },
    approvalGroupFilter: {
      title: 'Primary Approver/Approval Group',
      labels: toApprovalGroupApproverLabels(
        response?.approvers,
        response?.approvalGroups
      ),
    },
    jobCodeFilter: {
      title: 'Job Code-Description',
      labels: toLabelList(response?.jobCodes),
    },
    statusFilter: {
      title: 'Request Status',
      labels: response?.statuses?.length
        ? toLabelList(response.statuses)
        : DEFAULT_STATUS_LABELS,
      disabledLabels: ['Denied', 'Retraction Submitted', 'Retracted'],
    },
    requestTypeFilter: {
      title: 'Request Type',
      labels: response?.requestTypes?.length
        ? toLabelList(response.requestTypes, { withAll: false })
        : DEFAULT_REQUEST_TYPE_LABELS,
    },
  }
}

export interface CalendarViewState {
  data: CalendarViewResponse | null
  loading: boolean
  error: string | null
  /** requestId of the most recently dispatched getCalendarView call; used to ignore stale/aborted responses. */
  currentRequestId: string | null
  filters: {
    data: CalendarFiltersResponse | null
    loading: boolean
    error: string | null
  }
}

const initialState: CalendarViewState = {
  data: null,
  loading: false,
  error: null,
  currentRequestId: null,
  filters: {
    data: null,
    loading: false,
    error: null,
  },
}

/**
 * Thunk to fetch calendar week data with time off requests.
 * The `x-nuid` header is attached automatically by the axios request interceptor.
 * @param payload selectedDate (required) and optional filters
 */
export const getCalendarView = createAsyncThunk(
  'calendarView/getCalendarView',
  async (payload: CalendarViewPayload, { rejectWithValue, signal }) => {
    try {
      const url = endpoints.requester.calendarView
      const response = await api.post(url, payload, { signal })
      return response.data as CalendarViewResponse
    } catch (error: any) {
      return rejectWithValue(
        extractApiErrorMessage(error, 'Failed to fetch calendar view data')
      )
    }
  }
)

/**
 * Thunk to fetch filter dropdown options for the calendar view.
 * The `x-nuid` header is attached automatically by the axios request interceptor.
 */
export const getCalendarFilters = createAsyncThunk(
  'calendarView/getCalendarFilters',
  async (_arg, { rejectWithValue, signal }) => {
    try {
      const url = endpoints.requester.calendarFilters
      const response = await api.get(url, { signal })
      return response.data as CalendarFiltersResponse
    } catch (error: any) {
      return rejectWithValue(
        extractApiErrorMessage(error, 'Failed to fetch calendar filter options')
      )
    }
  }
)

const slice = createSlice({
  name: 'calendarView',
  initialState,
  reducers: {
    clearCalendarView: (state) => {
      state.data = null
      state.error = null
      state.loading = false
      state.currentRequestId = null
    },
    clearCalendarFilters: (state) => {
      state.filters.data = null
      state.filters.error = null
      state.filters.loading = false
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getCalendarView.pending, (state, action) => {
      state.currentRequestId = action.meta.requestId
      state.loading = true
      state.error = null
    })
    builder.addCase(getCalendarView.fulfilled, (state, action) => {
      // Ignore responses from a superseded request (e.g. React StrictMode's
      // dev-only double effect invocation aborts and replaces the first call).
      if (action.meta.requestId !== state.currentRequestId) return
      state.loading = false
      state.data = action.payload
      state.error = null
    })
    builder.addCase(getCalendarView.rejected, (state, action) => {
      if (action.meta.requestId !== state.currentRequestId) return
      state.loading = false
      state.error = action.payload as string
    })

    // Calendar filter dropdown options
    builder.addCase(getCalendarFilters.pending, (state) => {
      state.filters.loading = true
      state.filters.error = null
    })
    builder.addCase(getCalendarFilters.fulfilled, (state, action) => {
      state.filters.loading = false
      state.filters.data = action.payload
      state.filters.error = null
    })
    builder.addCase(getCalendarFilters.rejected, (state, action) => {
      state.filters.loading = false
      state.filters.error = action.payload as string
    })
  },
})

export const { clearCalendarView, clearCalendarFilters } = slice.actions
export default slice.reducer
